import { CodedError, Platform, UnavailabilityError } from 'expo';
import * as Application from 'expo-application';
import ServerRegistrationModule from '../ServerRegistrationModule';
import { computeNextBackoffInterval } from './backoff';
const updateDevicePushTokenUrl = 'https://exp.host/--/api/v2/push/updateDeviceToken';
const LAST_TOKEN_KEY = 'lastRegisteredDeviceToken';
// Force re-registration after 7 days even if nothing changed, in case the
// server lost the device record (cleanup, migration, etc.).
const REGISTRATION_TTL_MS = 7 * 24 * 60 * 60 * 1000;
async function getLastRegisteredTokenDataAsync() {
    try {
        if (!ServerRegistrationModule.getRegistrationInfoAsync) {
            return null;
        }
        const info = await ServerRegistrationModule.getRegistrationInfoAsync();
        if (!info) {
            return null;
        }
        const parsed = JSON.parse(info);
        return parsed?.[LAST_TOKEN_KEY] ?? null;
    } catch  {
        return null;
    }
}
async function setLastRegisteredTokenDataAsync(tokenData) {
    try {
        if (!ServerRegistrationModule.getRegistrationInfoAsync || !ServerRegistrationModule.setRegistrationInfoAsync) {
            return;
        }
        const info = await ServerRegistrationModule.getRegistrationInfoAsync();
        const existing = info ? JSON.parse(info) : {};
        existing[LAST_TOKEN_KEY] = tokenData;
        await ServerRegistrationModule.setRegistrationInfoAsync(JSON.stringify(existing));
    } catch  {
    // Best-effort — next app open will re-register
    }
}
/**
 * Returns `true` if the device token or metadata has changed since the last
 * successful registration, or if the check cannot be performed (fail-open).
 */ export async function hasDeviceTokenChangedAsync(token) {
    try {
        const development = await shouldUseDevelopmentNotificationService();
        const lastTokenData = await getLastRegisteredTokenDataAsync();
        if (lastTokenData == null) {
            return true;
        }
        const age = Date.now() - (lastTokenData.registeredAt ?? 0);
        if (age < 0 || age >= REGISTRATION_TTL_MS) {
            return true;
        }
        return token.data !== lastTokenData.deviceToken || Application.applicationId !== lastTokenData.appId || development !== lastTokenData.development || getTypeOfToken(token) !== lastTokenData.type;
    } catch  {
        return true;
    }
}
export async function updateDevicePushTokenAsync(signal, token) {
    const doUpdateDevicePushTokenAsync = async (retry)=>{
        const [development, deviceId] = await Promise.all([
            shouldUseDevelopmentNotificationService(),
            getDeviceIdAsync()
        ]);
        const body = {
            deviceId: deviceId.toLowerCase(),
            development,
            deviceToken: token.data,
            appId: Application.applicationId,
            type: getTypeOfToken(token)
        };
        try {
            const response = await fetch(updateDevicePushTokenUrl, {
                method: 'POST',
                headers: {
                    'content-type': 'application/json'
                },
                body: JSON.stringify(body),
                signal
            });
            // Help debug erroring servers
            if (!response.ok) {
                console.debug('[expo-notifications] Error encountered while updating the device push token with the server:', await response.text());
            }
            if (response.ok) {
                await setLastRegisteredTokenDataAsync({
                    deviceToken: token.data,
                    appId: Application.applicationId,
                    development,
                    type: getTypeOfToken(token),
                    registeredAt: Date.now()
                });
            }
            // Retry if request failed
            if (!response.ok) {
                retry();
            }
        } catch (error) {
            // Depending on the fetch implementation, an aborted request may reject
            // with `AbortError` or a native cancellation error.
            if (signal.aborted || typeof error === 'object' && error?.name === 'AbortError') {
                // An aborted request is not a failure, so don't log or retry it.
                return;
            }
            console.warn('[expo-notifications] Error thrown while updating the device push token with the server:', error);
            retry();
        }
    };
    let shouldTry = true;
    const retry = ()=>{
        shouldTry = true;
    };
    let retriesCount = 0;
    const initialBackoff = 500; // 0.5 s
    const backoffOptions = {
        maxBackoff: 2 * 60 * 1000
    };
    let nextBackoffInterval = computeNextBackoffInterval(initialBackoff, retriesCount, backoffOptions);
    while(shouldTry && !signal.aborted){
        // Will be set to true by `retry` if it's called
        shouldTry = false;
        await doUpdateDevicePushTokenAsync(retry);
        // Do not wait if we won't retry
        if (shouldTry && !signal.aborted) {
            nextBackoffInterval = computeNextBackoffInterval(initialBackoff, retriesCount, backoffOptions);
            retriesCount += 1;
            await new Promise((resolve)=>setTimeout(resolve, nextBackoffInterval));
        }
    }
}
// Same as in getExpoPushTokenAsync
async function getDeviceIdAsync() {
    try {
        if (!ServerRegistrationModule.getInstallationIdAsync) {
            throw new UnavailabilityError('ExpoServerRegistrationModule', 'getInstallationIdAsync');
        }
        return await ServerRegistrationModule.getInstallationIdAsync();
    } catch (e) {
        throw new CodedError('ERR_NOTIFICATIONS_DEVICE_ID', `Could not fetch the installation ID of the application: ${e}.`);
    }
}
// Same as in getExpoPushTokenAsync
function getTypeOfToken(devicePushToken) {
    switch(devicePushToken.type){
        case 'ios':
            return 'apns';
        case 'android':
            return 'fcm';
        // This probably will error on server, but let's make this function future-safe.
        default:
            return devicePushToken.type;
    }
}
// Same as in getExpoPushTokenAsync
async function shouldUseDevelopmentNotificationService() {
    if (Platform.OS === 'ios') {
        try {
            const notificationServiceEnvironment = await Application.getIosPushNotificationServiceEnvironmentAsync();
            if (notificationServiceEnvironment === 'development') {
                return true;
            }
        } catch  {
        // We can't do anything here, we'll fallback to false then.
        }
    }
    return false;
}

//# sourceMappingURL=updateDevicePushTokenAsync.js.map
